/**
 * AdminCockpit — Centre de pilotage CONFILEX
 *
 * Tour de contrôle premium qui consomme tous les endpoints admin :
 * - Dashboard meta (KPIs cross-modules)
 * - Channels status (LIVE/MOCK)
 * - Agents IA (10 agents, enable/disable/run)
 * - Queue de tâches (drafts à valider)
 * - SEO Agent (génération articles + recommandations)
 * - SEA Agent (campagnes + budgets + alertes)
 * - Audit Log viewer (qui/quoi/quand)
 * - Healthcheck intégrations
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { apiClient } from "@/lib/api";
import { toast } from "sonner";
import "./AdminCockpit.css";

const TABS = [
  { id: "overview", label: "Vue d'ensemble" },
  { id: "agents", label: "Agents IA" },
  { id: "tasks", label: "Validations en attente" },
  { id: "seo", label: "SEO" },
  { id: "sea", label: "SEA Google Ads" },
  { id: "channels", label: "Canaux" },
  { id: "audit", label: "Audit Log" },
  { id: "health", label: "Santé" },
];

const authHeaders = () => ({}); // Deprecated: auth via HttpOnly cookies (apiClient)

export default function AdminCockpit() {
  const [activeTab, setActiveTab] = useState("overview");
  const [dashboard, setDashboard] = useState(null);
  const [tasksPending, setTasksPending] = useState(0);

  // Charge dashboard meta au mount + toutes les 30s
  const loadDashboard = useCallback(async () => {
    try {
      const r = await apiClient.get(`/admin/dashboard`);
      setDashboard(r.data);
      setTasksPending(r.data.agents?.tasks_pending_approval || 0);
    } catch (e) {
      // Silencieux ; le rendu par section gérera l'erreur
    }
  }, []);

  useEffect(() => {
    loadDashboard();
    const id = setInterval(loadDashboard, 30000);
    return () => clearInterval(id);
  }, [loadDashboard]);

  return (
    <div className="cockpit-root" data-testid="admin-cockpit-root">
      <div className="cockpit-shell">
        <header className="cockpit-header">
          <div>
            <div className="cockpit-eyebrow">Centre de pilotage</div>
            <h1 className="cockpit-title">CONFILEX Cockpit</h1>
            <p className="cockpit-subtitle">
              Tour de contrôle unifiée — agents IA, acquisition, communication, conformité.
            </p>
          </div>
          {dashboard && <OverviewMini d={dashboard} />}
        </header>

        <MaintenanceTogglePanel />

        <nav className="cockpit-tabs" role="tablist">
          {TABS.map((t) => (
            <button
              key={t.id}
              role="tab"
              data-testid={`cockpit-tab-${t.id}`}
              className={`cockpit-tab ${activeTab === t.id ? "active" : ""}`}
              onClick={() => setActiveTab(t.id)}
            >
              {t.label}
              {t.id === "tasks" && tasksPending > 0 && (
                <span className="cockpit-tab-count">{tasksPending}</span>
              )}
            </button>
          ))}
        </nav>

        <main>
          {activeTab === "overview" && <OverviewPanel dashboard={dashboard} reload={loadDashboard} />}
          {activeTab === "agents" && <AgentsPanel onChange={loadDashboard} />}
          {activeTab === "tasks" && <TasksPanel onChange={loadDashboard} />}
          {activeTab === "seo" && <SeoPanel />}
          {activeTab === "sea" && <SeaPanel />}
          {activeTab === "channels" && <ChannelsPanel />}
          {activeTab === "audit" && <AuditPanel />}
          {activeTab === "health" && <HealthPanel />}
        </main>
      </div>
    </div>
  );
}

/* ---------------------- Mini résumé en header ---------------------- */
function OverviewMini({ d }) {
  return (
    <div style={{ display: "flex", gap: 22, alignItems: "center", fontSize: 12, color: "#BFC8C2" }}>
      <span>
        <strong style={{ color: "#d4af7a" }}>{d.users?.total ?? 0}</strong> users
      </span>
      <span>
        <strong style={{ color: "#d4af7a" }}>{d.dossiers?.workspaces ?? 0}</strong> dossiers
      </span>
      <span>
        <strong style={{ color: "#d4af7a" }}>{d.revenue?.total_eur?.toFixed(0) ?? 0}€</strong>
      </span>
      <span>
        <strong style={{ color: "#d4af7a" }}>{d.channels?.live ?? 0}/{d.channels?.total ?? 0}</strong> canaux
      </span>
    </div>
  );
}

/* ---------------------- OVERVIEW PANEL ---------------------- */
function OverviewPanel({ dashboard }) {
  if (!dashboard) return <Skeleton lines={10} />;
  const cards = [
    { label: "Utilisateurs", value: dashboard.users.total, sub: `${dashboard.users.clients} clients · ${dashboard.users.experts_total} spécialistes` },
    { label: "Spécialistes vérifiés", value: dashboard.users.experts_verified, sub: `sur ${dashboard.users.experts_total} inscrits` },
    { label: "Workspaces", value: dashboard.dossiers.workspaces, sub: `${dashboard.dossiers.missions_active} missions actives` },
    { label: "RDV", value: dashboard.dossiers.appointments_confirmed, sub: `${dashboard.dossiers.appointments_pending} en attente` },
    { label: "Revenue total", value: `${dashboard.revenue.total_eur.toFixed(0)} €`, sub: `${dashboard.revenue.paid_count} paiements` },
    { label: "Appels vocaux", value: dashboard.voice.calls_total, sub: `${dashboard.voice.calls_completed} aboutis` },
    { label: "Agents actifs", value: `${dashboard.agents.enabled}/${dashboard.agents.total}`, sub: `${dashboard.agents.tasks_pending_approval} tâches à valider` },
    { label: "Canaux LIVE", value: `${dashboard.channels.live}/${dashboard.channels.total}`, sub: `${dashboard.channels.mocked} en mock` },
  ];

  return (
    <div data-testid="cockpit-overview">
      <div className="kpi-grid">
        {cards.map((c, i) => (
          <div key={i} className="kpi-card" data-testid={`kpi-${i}`}>
            <div className="kpi-label">{c.label}</div>
            <div className="kpi-value">{c.value}</div>
            <div className="kpi-delta">{c.sub}</div>
          </div>
        ))}
      </div>
      <div className="section">
        <div className="section-h"><h3>État synthétique</h3></div>
        <div style={{ color: "#a8b1ce", fontSize: 13, lineHeight: 1.7 }}>
          Données actualisées toutes les 30 secondes. Chaque module dispose de son propre panneau dédié dans les onglets ci-dessus.
        </div>
      </div>
    </div>
  );
}

/* ---------------------- AGENTS PANEL ---------------------- */
function AgentsPanel({ onChange }) {
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await apiClient.get(`/admin/agents`);
      setAgents(r.data.agents || []);
    } catch (e) {
      toast.error("Impossible de charger les agents");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggle = async (name, currentStatus) => {
    const route = currentStatus === "enabled" ? "disable" : "enable";
    try {
      await apiClient.post(`/admin/agents/${name}/${route}`, {});
      toast.success(`Agent ${name} ${route === "enable" ? "activé" : "désactivé"}`);
      await load();
      onChange?.();
    } catch (e) {
      toast.error(`Échec ${route}: ${e.response?.data?.detail || e.message}`);
    }
  };

  const runNow = async (name) => {
    try {
      const r = await apiClient.post(`/admin/agents/${name}/run`, {});
      toast.success(`Cycle exécuté — ${JSON.stringify(r.data.result).slice(0, 80)}`);
      onChange?.();
    } catch (e) {
      toast.error(`Échec: ${e.response?.data?.detail || e.message}`);
    }
  };

  if (loading) return <Skeleton lines={10} />;

  return (
    <div data-testid="cockpit-agents">
      <div className="section">
        <div className="section-h">
          <h3>10 Agents IA</h3>
          <span className="section-h-meta">
            {agents.filter((a) => a.status === "enabled").length} actif(s) · {agents.filter((a) => a.status === "needs_config").length} en attente config
          </span>
        </div>
        {agents.map((a) => (
          <div key={a.name} className="row" data-testid={`agent-row-${a.name}`}>
            <div className="row-main">
              <div className="row-title">
                {a.display_name}{" "}
                <StatusBadge status={a.status} />
              </div>
              <div className="row-sub">{a.description}</div>
              {a.missing_env?.length > 0 && (
                <div className="row-sub" style={{ marginTop: 4, color: "#e87a7a" }}>
                  Variables manquantes : {a.missing_env.join(", ")}
                </div>
              )}
              {a.last_run && (
                <div className="row-sub" style={{ marginTop: 2 }}>
                  Dernier cycle : {new Date(a.last_run).toLocaleString("fr-FR")}{" "}
                  · {a.tasks_generated || 0} tâches générées · {a.tasks_approved || 0} approuvées
                </div>
              )}
            </div>
            <div className="row-actions">
              <button
                className={`btn btn-sm ${a.status === "enabled" ? "btn-danger" : "btn-primary"}`}
                onClick={() => toggle(a.name, a.status)}
                data-testid={`agent-toggle-${a.name}`}
                disabled={a.missing_env?.length > 0 && a.status !== "enabled"}
                title={a.missing_env?.length > 0 ? "Configurez les variables d'env" : ""}
              >
                {a.status === "enabled" ? "Désactiver" : "Activer"}
              </button>
              <button
                className="btn btn-sm"
                onClick={() => runNow(a.name)}
                disabled={a.status !== "enabled"}
                data-testid={`agent-run-${a.name}`}
              >
                Exécuter
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------------------- TASKS PANEL ---------------------- */
function TasksPanel({ onChange }) {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await apiClient.get(`/admin/agents/tasks?status=pending&limit=100`);
      setTasks(r.data.items || []);
    } catch (e) {
      toast.error("Erreur chargement tâches");
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const approve = async (id) => {
    try {
      await apiClient.post(`/admin/agents/tasks/${id}/approve`, {});
      toast.success("Tâche approuvée");
      await load(); onChange?.();
    } catch (e) {
      toast.error("Échec approbation");
    }
  };
  const reject = async (id) => {
    const reason = prompt("Raison du refus (optionnel) :", "");
    try {
      await apiClient.post(`/admin/agents/tasks/${id}/reject`, { reason });
      toast.success("Tâche refusée");
      await load(); onChange?.();
    } catch (e) {
      toast.error("Échec");
    }
  };

  if (loading) return <Skeleton lines={6} />;
  if (tasks.length === 0) return <div className="section"><div className="empty">Aucune tâche en attente de validation. ✨</div></div>;

  return (
    <div data-testid="cockpit-tasks">
      {tasks.map((t) => (
        <div key={t.id} className="section" data-testid={`task-${t.id}`}>
          <div className="section-h">
            <h3>{t.title}</h3>
            <span className="section-h-meta">
              {t.agent} · {t.kind} · {new Date(t.created_at).toLocaleString("fr-FR")}
            </span>
          </div>
          <div className="code-preview">{t.body || "(pas de contenu)"}</div>
          {t.target_channel && (
            <div className="row-sub" style={{ marginTop: 10 }}>
              Canal cible : <span className="badge badge-info">{t.target_channel}</span>
            </div>
          )}
          <div style={{ marginTop: 14, display: "flex", gap: 8 }}>
            <button className="btn btn-primary btn-sm" onClick={() => approve(t.id)} data-testid={`task-approve-${t.id}`}>
              Approuver
            </button>
            <button className="btn btn-danger btn-sm" onClick={() => reject(t.id)} data-testid={`task-reject-${t.id}`}>
              Refuser
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

/* ---------------------- SEO PANEL ---------------------- */
function SeoPanel() {
  const [perf, setPerf] = useState(null);
  const [recos, setRecos] = useState([]);
  const [seed, setSeed] = useState("");
  const [keywords, setKeywords] = useState([]);
  const [drafts, setDrafts] = useState([]);
  const [genLoading, setGenLoading] = useState(false);

  const load = useCallback(async () => {
    try {
      const [p, r, d] = await Promise.all([
        apiClient.get(`/admin/seo/performance`),
        apiClient.get(`/admin/seo/recommendations`),
        apiClient.get(`/admin/seo/articles/drafts?limit=10`),
      ]);
      setPerf(p.data); setRecos(r.data.recommendations || []); setDrafts(d.data.items || []);
    } catch (e) { toast.error("Erreur chargement SEO"); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const research = async () => {
    if (!seed.trim()) return;
    try {
      const r = await apiClient.post(`/admin/seo/keywords/research`, { seed: seed.trim(), limit: 12 });
      setKeywords(r.data.results || []);
    } catch (e) { toast.error("Recherche échouée"); }
  };

  const generateArticle = async (kw) => {
    setGenLoading(true);
    try {
      const r = await apiClient.post(`/admin/seo/articles/generate`, { keyword: kw, target_word_count: 1500 });
      toast.success(`Article généré : ${r.data.article.title}`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Génération échouée");
    } finally { setGenLoading(false); }
  };

  const publish = async (id) => {
    try {
      const r = await apiClient.post(`/admin/seo/articles/${id}/approve`, {});
      const warnings = r.data.warnings || [];
      if (warnings.length) toast.warning(warnings.join(" · "));
      else toast.success("Article publié");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail); }
  };

  return (
    <div data-testid="cockpit-seo">
      {perf && (
        <div className="kpi-grid">
          <KPI label="Publiés" value={perf.totals.published} sub={`${perf.totals.ai_published} IA · ${perf.totals.human_published} humain`} />
          <KPI label="Drafts en attente" value={perf.totals.drafts} sub="à valider" />
          <KPI label="Google Search Console" value={perf.gsc.mode} sub={perf.gsc.mode === "live" ? "Synchronisé" : "À configurer"} />
          <KPI label="Recommandations actives" value={recos.length} sub="optimisations détectées" />
        </div>
      )}

      <div className="section">
        <div className="section-h"><h3>Recherche de mots-clés</h3></div>
        <div style={{ display: "flex", gap: 8 }}>
          <input
            data-testid="seo-keyword-input"
            value={seed}
            onChange={(e) => setSeed(e.target.value)}
            placeholder="Ex: mise en demeure locataire"
            style={{ flex: 1, background: "rgba(11,18,38,0.5)", border: "1px solid rgba(212,175,122,0.2)", color: "#e9ecf5", padding: "8px 12px", borderRadius: 8, fontSize: 13 }}
          />
          <button className="btn btn-primary" onClick={research} data-testid="seo-research-btn">Rechercher</button>
        </div>
        {keywords.length > 0 && (
          <div style={{ marginTop: 14 }}>
            {keywords.map((k, i) => (
              <div key={i} className="row">
                <div className="row-main">
                  <div className="row-title">{k.keyword}</div>
                  <div className="row-sub">
                    Volume {k.search_volume}/mois · Difficulty {k.difficulty}/100 · Intent: {k.intent}
                  </div>
                </div>
                <div className="row-actions">
                  <button className="btn btn-sm" onClick={() => generateArticle(k.keyword)} disabled={genLoading} data-testid={`seo-gen-${i}`}>
                    Générer un article
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="section">
        <div className="section-h"><h3>Drafts à valider</h3></div>
        {drafts.length === 0 ? (
          <div className="empty">Aucun draft en attente.</div>
        ) : drafts.map((d) => (
          <div key={d.id} className="row" data-testid={`seo-draft-${d.id}`}>
            <div className="row-main">
              <div className="row-title">{d.title}</div>
              <div className="row-sub">
                {(d.body || "").split(" ").length} mots · {d.seo_meta_description ? "✓ meta" : "✗ pas de meta"} · {(d.seo_keywords || []).length} keywords
              </div>
            </div>
            <div className="row-actions">
              <button className="btn btn-primary btn-sm" onClick={() => publish(d.id)} data-testid={`seo-publish-${d.id}`}>
                Publier
              </button>
            </div>
          </div>
        ))}
      </div>

      <div className="section">
        <div className="section-h"><h3>Recommandations</h3></div>
        {recos.length === 0 ? (
          <div className="empty">Aucune recommandation — votre SEO est en ordre.</div>
        ) : recos.map((r, i) => (
          <div key={i} className="row">
            <div className="row-main">
              <div className="row-title">
                <span className={`badge badge-${r.severity === "critical" ? "critical" : r.severity === "important" ? "warning" : "info"}`}>{r.severity}</span>{" "}
                {r.title}
              </div>
              <div className="row-sub">{r.action}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------------------- SEA PANEL ---------------------- */
function SeaPanel() {
  const [dash, setDash] = useState(null);
  const [alerts, setAlerts] = useState([]);

  const load = useCallback(async () => {
    try {
      const [d, a] = await Promise.all([
        apiClient.get(`/admin/sea/dashboard`),
        apiClient.get(`/admin/sea/alerts`),
      ]);
      setDash(d.data); setAlerts(a.data.alerts || []);
    } catch (e) { toast.error("Erreur SEA"); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const weeklyReport = async () => {
    try {
      const r = await apiClient.post(`/admin/sea/report/weekly`, {});
      toast.success("Rapport hebdo généré");
      // Ouverture en text/plain via Blob URL — pas d'injection HTML possible (XSS-safe)
      const blob = new Blob([r.data.markdown || ""], { type: "text/plain;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener,noreferrer");
      // Révocation différée pour laisser le temps au nouvel onglet de charger
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { toast.error("Erreur génération"); }
  };

  if (!dash) return <Skeleton lines={8} />;
  const s = dash.summary;

  return (
    <div data-testid="cockpit-sea">
      <div className="kpi-grid">
        <KPI label="Coût total 28j" value={`${s.total_cost_28d_eur.toFixed(0)} €`} />
        <KPI label="Conversions 28j" value={s.total_conversions_28d} />
        <KPI label="CPL moyen" value={`${s.avg_cpl_eur.toFixed(2)} €`} />
        <KPI label="ROAS" value={`${s.avg_roas.toFixed(2)}x`} />
        <KPI label="Campagnes actives" value={s.campaigns_active} sub={`${s.campaigns_draft} en draft`} />
        <KPI label="Google Ads" value={dash.google_ads.mode} sub={dash.google_ads.configured ? "API connectée" : "À configurer"} />
      </div>

      <div className="section">
        <div className="section-h">
          <h3>Alertes actives</h3>
          <button className="btn btn-sm btn-primary" onClick={weeklyReport} data-testid="sea-weekly-btn">Rapport hebdo</button>
        </div>
        {alerts.length === 0 ? (
          <div className="empty">Aucune alerte. Tout va bien.</div>
        ) : alerts.map((a, i) => (
          <div key={i} className="row">
            <div className="row-main">
              <div className="row-title">
                <span className={`badge badge-${a.severity}`}>{a.severity}</span>{" "}
                {a.campaign_name} — {a.kind}
              </div>
              <div className="row-sub">{a.action_suggested || JSON.stringify(a)}</div>
            </div>
          </div>
        ))}
      </div>

      {dash.top_campaigns?.length > 0 && (
        <div className="section">
          <div className="section-h"><h3>Top campagnes (28j)</h3></div>
          {dash.top_campaigns.map((c) => (
            <div key={c.campaign_id} className="row">
              <div className="row-main">
                <div className="row-title">{c.name}</div>
                <div className="row-sub">
                  {c.conversions_28d} conv · CPL {c.cpl_avg.toFixed(2)}€ · ROAS {c.roas.toFixed(2)}x
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---------------------- CHANNELS PANEL ---------------------- */
function ChannelsPanel() {
  const [channels, setChannels] = useState({});
  const load = useCallback(async () => {
    try {
      const r = await apiClient.get(`/admin/channels`);
      setChannels(r.data.channels || {});
    } catch (e) { toast.error("Erreur"); }
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="cockpit-channels">
      <div className="section">
        <div className="section-h">
          <h3>État des canaux de communication</h3>
          <span className="section-h-meta">
            {Object.values(channels).filter((c) => c.mode === "live").length}/{Object.keys(channels).length} en LIVE
          </span>
        </div>
        {Object.entries(channels).map(([name, info]) => (
          <div key={name} className="row" data-testid={`channel-${name}`}>
            <div className="row-main">
              <div className="row-title">
                {name} <span className={`badge badge-${info.mode}`}>{info.mode}</span>
              </div>
              {info.missing_env?.length > 0 && (
                <div className="row-sub">Variables manquantes : {info.missing_env.join(", ")}</div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------------------- AUDIT LOG PANEL ---------------------- */
function AuditPanel() {
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [filterAction, setFilterAction] = useState("");

  const load = useCallback(async () => {
    try {
      const params = new URLSearchParams({ limit: "60" });
      if (filterAction) params.set("action", filterAction);
      const [l, s] = await Promise.all([
        apiClient.get(`/admin/audit-logs?${params}`),
        apiClient.get(`/admin/audit-logs/stats`),
      ]);
      setLogs(l.data.items || []); setStats(s.data);
    } catch (e) { toast.error("Erreur audit"); }
  }, [filterAction]);
  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="cockpit-audit">
      {stats && (
        <div className="kpi-grid">
          <KPI label="Logs total" value={stats.total_logs} />
          <KPI label="Critiques" value={stats.critical_count} />
          <KPI label="Top action" value={stats.top_actions?.[0]?.action || "-"} sub={`${stats.top_actions?.[0]?.count || 0} occurrences`} />
          <KPI label="Top acteur" value={stats.top_actors?.[0]?.actor || "-"} sub={`${stats.top_actors?.[0]?.count || 0} actions`} />
        </div>
      )}
      <div className="section">
        <div className="section-h">
          <h3>Journal des actions admin</h3>
          <input
            value={filterAction}
            onChange={(e) => setFilterAction(e.target.value)}
            placeholder="Filtrer (ex: admin.sea)"
            style={{ background: "rgba(11,18,38,0.5)", border: "1px solid rgba(212,175,122,0.2)", color: "#e9ecf5", padding: "6px 10px", borderRadius: 6, fontSize: 12 }}
            data-testid="audit-filter"
          />
        </div>
        {logs.length === 0 ? <div className="empty">Aucun log.</div> :
          logs.map((l) => (
            <div key={l.id} className="row">
              <div className="row-main">
                <div className="row-title">
                  {l.action}{" "}
                  <span className={`badge badge-${l.severity === "critical" ? "critical" : l.severity === "warning" ? "warning" : "info"}`}>
                    {l.severity || "info"}
                  </span>
                </div>
                <div className="row-sub">
                  {l.actor_email} · {l.resource_type}/{l.resource_id?.slice(0, 24)}... · {new Date(l.created_at).toLocaleString("fr-FR")} · {l.ip}
                </div>
              </div>
            </div>
          ))}
      </div>
    </div>
  );
}

/* ---------------------- HEALTH PANEL ---------------------- */
function HealthPanel() {
  const [health, setHealth] = useState(null);
  const load = useCallback(async () => {
    try {
      const r = await apiClient.get(`/health/full`);
      setHealth(r.data);
    } catch (e) { toast.error("Erreur health"); }
  }, []);
  useEffect(() => { load(); const i = setInterval(load, 30000); return () => clearInterval(i); }, [load]);

  if (!health) return <Skeleton lines={6} />;
  return (
    <div data-testid="cockpit-health">
      <div className="section">
        <div className="section-h">
          <h3>Santé des intégrations</h3>
          <span className={`badge badge-${health.status === "ok" ? "live" : "warning"}`}>
            {health.status}
          </span>
        </div>
        <div className="row-sub" style={{ marginBottom: 10 }}>
          Version {health.version} · Boot {new Date(health.boot_time).toLocaleString("fr-FR")}
        </div>
        {Object.entries(health.integrations || {}).map(([name, i]) => (
          <div key={name} className="row" data-testid={`health-${name}`}>
            <div className="row-main">
              <div className="row-title">
                {name}{" "}
                {!i.configured ? (
                  <span className="badge badge-disabled">non configuré</span>
                ) : i.ok === false ? (
                  <span className="badge badge-critical">en erreur</span>
                ) : i.ok === true ? (
                  <span className="badge badge-live">LIVE</span>
                ) : (
                  <span className="badge badge-info">configuré</span>
                )}
              </div>
              <div className="row-sub">
                {i.latency_ms != null ? `${i.latency_ms} ms` : ""}
                {i.error ? ` · ${i.error}` : ""}
                {i.status_code ? ` · HTTP ${i.status_code}` : ""}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------------------- Petits composants ---------------------- */
function KPI({ label, value, sub }) {
  return (
    <div className="kpi-card">
      <div className="kpi-label">{label}</div>
      <div className="kpi-value">{value}</div>
      {sub && <div className="kpi-delta">{sub}</div>}
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    enabled: { cls: "badge-enabled", label: "actif" },
    disabled: { cls: "badge-disabled", label: "désactivé" },
    paused: { cls: "badge-warning", label: "en pause" },
    needs_config: { cls: "badge-needs", label: "à configurer" },
  };
  const v = map[status] || { cls: "badge-info", label: status };
  return <span className={`badge ${v.cls}`}>{v.label}</span>;
}

function Skeleton({ lines = 5 }) {
  return (
    <div className="section">
      {Array.from({ length: lines }).map((_, i) => <div key={i} className="skeleton" />)}
    </div>
  );
}


/**
 * MaintenanceTogglePanel — Switch admin pour activer/désactiver le mode
 * maintenance global. Persiste dans `platform_config.maintenance_mode`.
 *
 * Visible UNIQUEMENT à un admin connecté (la route /admin/* est elle-même
 * whitelistée par MaintenanceGate, donc l'admin reste joignable même en
 * maintenance pour pouvoir la désactiver).
 */
function MaintenanceTogglePanel() {
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await apiClient.get(`/maintenance/status`);
      setState(r.data);
      setMessage(r.data?.message || "");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggle = async (nextEnabled) => {
    setSubmitting(true);
    try {
      const r = await apiClient.post(
        `/admin/maintenance`,
        { enabled: nextEnabled, message: message || undefined },
      );
      setState(r.data);
      setMessage(r.data.message || "");
    } catch (e) {
      window.alert(e?.response?.data?.detail || "Erreur lors du basculement.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return null;
  const enabled = !!state?.enabled;

  return (
    <div
      className="section"
      data-testid="maintenance-toggle-panel"
      style={{
        margin: "0 0 20px",
        padding: "20px 24px",
        borderRadius: 16,
        border: enabled ? "1px solid #C9A961" : "1px solid rgba(255,255,255,0.08)",
        background: enabled
          ? "linear-gradient(135deg, rgba(196,164,104,0.18), rgba(196,164,104,0.06))"
          : "rgba(255,255,255,0.03)",
        display: "flex",
        flexDirection: "column",
        gap: 14,
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16, flexWrap: "wrap" }}>
        <div style={{ minWidth: 280 }}>
          <p style={{ fontSize: 10, letterSpacing: "0.24em", textTransform: "uppercase", color: "#C9A961", margin: 0 }}>
            Mode maintenance global
          </p>
          <h3 style={{ fontFamily: "Marcellus, serif", fontSize: 22, color: "#fff", margin: "6px 0 4px" }}>
            {enabled ? "Site public en MAINTENANCE" : "Site public actif"}
          </h3>
          <p style={{ fontSize: 12, color: "rgba(255,255,255,0.65)", margin: 0, maxWidth: 560, lineHeight: 1.5 }}>
            Lorsqu'activé, toutes les routes publiques redirigent vers une page maintenance institutionnelle. Les routes <code>/admin/*</code> et <code>/login</code> restent accessibles.
          </p>
        </div>
        <button
          type="button"
          disabled={submitting}
          onClick={() => toggle(!enabled)}
          data-testid="maintenance-toggle-btn"
          style={{
            padding: "11px 22px",
            borderRadius: 999,
            border: "none",
            cursor: submitting ? "wait" : "pointer",
            background: enabled ? "#fff" : "#C9A961",
            color: "#0B3A2A",
            fontWeight: 600,
            fontSize: 13,
            letterSpacing: "0.04em",
          }}
        >
          {submitting ? "…" : enabled ? "Désactiver la maintenance" : "Activer la maintenance"}
        </button>
      </div>
      <label style={{ fontSize: 11, color: "rgba(255,255,255,0.55)", textTransform: "uppercase", letterSpacing: "0.2em" }}>
        Message affiché aux visiteurs (optionnel)
      </label>
      <textarea
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        rows={2}
        placeholder="Nous procédons à une intervention technique…"
        data-testid="maintenance-message-input"
        style={{
          width: "100%",
          background: "rgba(255,255,255,0.04)",
          color: "#fff",
          border: "1px solid rgba(255,255,255,0.12)",
          borderRadius: 10,
          padding: "10px 12px",
          fontSize: 13,
          resize: "vertical",
        }}
      />
      {state?.started_at && (
        <p style={{ fontSize: 11, color: "rgba(255,255,255,0.45)", margin: 0 }}>
          Activé depuis : {new Date(state.started_at).toLocaleString("fr-FR")}
        </p>
      )}
    </div>
  );
}
